import React, { useState, useEffect, useMemo } from 'react';
import {
    FaKey, FaUser, FaLock, FaGlobe, FaServer, FaHashtag, FaFileAlt,
    FaEye, FaEyeSlash, FaSync, FaCopy, FaCheck, FaShieldAlt, FaBolt, FaTag,
    FaNetworkWired, FaDatabase
} from 'react-icons/fa';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import './FormModal.css';

// Character sets for password generation
const CHAR_SETS = {
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

// Configuración de campos por categoría
// Define qué campos mostrar para cada tipo de credencial
const CATEGORY_FIELDS = {
    // Servidores: todos los campos
    'Servidores': {
        fields: ['username', 'password', 'ip_address', 'puerto', 'dominio', 'ambiente'],
        icon: FaServer,
        description: 'Credenciales de acceso a servidores Windows/Linux'
    },
    // Redes: equipos de red (routers, switches, etc)
    'Redes': {
        fields: ['username', 'password', 'ip_address', 'puerto'],
        icon: FaNetworkWired,
        description: 'Credenciales de equipos de red (routers, switches, APs)'
    },
    // Credencial Web: URLs y ambiente
    'Credencial Web': {
        fields: ['username', 'password', 'url', 'ambiente'],
        icon: FaGlobe,
        description: 'Credenciales de plataformas web y SaaS'
    },
    // Bases de Datos: conexión completa
    'Bases de Datos': {
        fields: ['username', 'password', 'ip_address', 'puerto', 'dominio', 'ambiente'],
        icon: FaDatabase,
        description: 'Credenciales de bases de datos'
    },
    // Servicios: APIs y similares
    'Servicios': {
        fields: ['username', 'password', 'url', 'puerto'],
        icon: FaServer,
        description: 'Credenciales de servicios y APIs'
    },
    // Otros: mostrar todo
    'Otros': {
        fields: ['username', 'password', 'url', 'ip_address', 'puerto', 'dominio', 'ambiente'],
        icon: FaKey,
        description: 'Otras credenciales'
    }
};

// Campos por defecto (cuando no hay categoría seleccionada)
const DEFAULT_FIELDS = ['username', 'password', 'url', 'ip_address', 'puerto', 'dominio', 'ambiente'];

const CredentialModal = ({ credential, categories, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        username: '',
        password: '',
        url: '',
        ip_address: '',
        puerto: '',
        dominio: '',
        ambiente: '',
        notas: '',
        tags: [],
        categoria_id: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordLength, setPasswordLength] = useState(16);
    const [passwordOptions, setPasswordOptions] = useState({
        lowercase: true,
        uppercase: true,
        numbers: true,
        symbols: true
    });
    const [showGenerator, setShowGenerator] = useState(false);
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [tagInput, setTagInput] = useState('');

    const isEditing = !!credential;

    // Obtener el nombre de la categoría seleccionada
    const selectedCategory = useMemo(() => {
        if (!formData.categoria_id) return null;
        return categories.find(c => c.id === parseInt(formData.categoria_id));
    }, [formData.categoria_id, categories]);

    // Determinar qué campos mostrar basado en la categoría
    const visibleFields = useMemo(() => {
        if (!selectedCategory) return DEFAULT_FIELDS;
        const config = CATEGORY_FIELDS[selectedCategory.nombre];
        return config?.fields || DEFAULT_FIELDS;
    }, [selectedCategory]);

    // Obtener descripción de la categoría
    const categoryDescription = useMemo(() => {
        if (!selectedCategory) return null;
        return CATEGORY_FIELDS[selectedCategory.nombre]?.description;
    }, [selectedCategory]);

    useEffect(() => {
        if (credential) {
            fetchCredentialDetails(credential.id);
        }
    }, [credential]);

    const fetchCredentialDetails = async (id) => {
        try {
            const response = await axiosInstance.get(`/api/credentials/${id}`);
            const data = response.data;
            setFormData({
                nombre: data.nombre || '',
                username: data.username || '',
                password: data.password || '',
                url: data.url || '',
                ip_address: data.ip_address || '',
                puerto: data.puerto || '',
                dominio: data.dominio || '',
                ambiente: data.ambiente || '',
                notas: data.notas || '',
                tags: data.tags || [],
                categoria_id: data.categoria_id || ''
            });
        } catch (err) {
            console.error('Error fetching credential details:', err);
        }
    };

    useEffect(() => {
        calculatePasswordStrength(formData.password);
    }, [formData.password]);

    const calculatePasswordStrength = (password) => {
        if (!password) { setPasswordStrength(0); return; }
        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (password.length >= 12) strength += 1;
        if (password.length >= 16) strength += 1;
        if (/[a-z]/.test(password)) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
        setPasswordStrength(Math.min((strength / 7) * 100, 100));
    };

    const getStrengthColor = () => {
        if (passwordStrength < 30) return '#ef4444';
        if (passwordStrength < 60) return '#f59e0b';
        if (passwordStrength < 80) return '#10b981';
        return '#059669';
    };

    const getStrengthLabel = () => {
        if (passwordStrength < 30) return 'Débil';
        if (passwordStrength < 60) return 'Regular';
        if (passwordStrength < 80) return 'Fuerte';
        return 'Muy Fuerte';
    };

    const generatePassword = () => {
        let chars = '';
        Object.entries(passwordOptions).forEach(([key, enabled]) => {
            if (enabled) chars += CHAR_SETS[key];
        });
        if (!chars) chars = CHAR_SETS.lowercase + CHAR_SETS.uppercase;

        let password = '';
        const array = new Uint32Array(passwordLength);
        crypto.getRandomValues(array);
        for (let i = 0; i < passwordLength; i++) {
            password += chars[array[i] % chars.length];
        }
        setFormData(prev => ({ ...prev, password }));
        setShowPassword(true);
    };

    const copyPassword = async () => {
        if (formData.password) {
            await navigator.clipboard.writeText(formData.password);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            if (!formData.tags.includes(tagInput.trim())) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, tagInput.trim()]
                }));
            }
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const url = isEditing
                ? `/api/credentials/${credential.id}`
                : `/api/credentials`;

            const method = isEditing ? 'put' : 'post';
            await axiosInstance[method](url, {
                ...formData,
                puerto: formData.puerto ? parseInt(formData.puerto) : null,
                categoria_id: formData.categoria_id || null
            });

            onSave();
        } catch (err) {
            console.error('Error saving credential:', err);
            setError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    const ambientes = ['Producción', 'Desarrollo', 'QA', 'Staging', 'Testing'];

    // Helper para verificar si un campo debe mostrarse
    const showField = (field) => visibleFields.includes(field);

    return (
        <Modal isOpen={true} onClose={onClose} size="xl">
            <Modal.Header onClose={onClose}>
                {isEditing ? 'Editar Credencial' : 'Nueva Credencial'}
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="form-modal-error mb-4">
                        <FaShieldAlt />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} id="credential-form">
                    {/* Información básica - siempre visible */}
                    <div className="form-modal-section">
                        <h3><FaKey /> Información de la Credencial</h3>
                        <div className="form-modal-grid">
                            <div className="form-modal-group">
                                <label htmlFor="categoria_id">
                                    <FaTag className="input-icon" /> Categoría
                                </label>
                                <select
                                    id="categoria_id"
                                    name="categoria_id"
                                    value={formData.categoria_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione categoría</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                                    ))}
                                </select>
                                {categoryDescription && (
                                    <span className="text-xs text-gray-500 mt-1">{categoryDescription}</span>
                                )}
                            </div>

                            <div className="form-modal-group">
                                <label htmlFor="nombre">
                                    <FaKey className="input-icon" /> Nombre
                                </label>
                                <input
                                    type="text"
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder={selectedCategory ? `Ej: ${selectedCategory.nombre} - Producción` : 'Nombre descriptivo'}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Credenciales de acceso - siempre visible */}
                    <div className="form-modal-section">
                        <h3><FaLock /> Credenciales de Acceso</h3>
                        <div className="form-modal-grid">
                            {showField('username') && (
                                <div className="form-modal-group">
                                    <label htmlFor="username">
                                        <FaUser className="input-icon" /> Usuario
                                    </label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        placeholder="usuario o admin"
                                    />
                                </div>
                            )}

                            {showField('password') && (
                                <div className="form-modal-group">
                                    <label htmlFor="password">
                                        <FaLock className="input-icon" /> Contraseña
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            className="pr-24"
                                        />
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={copyPassword}
                                                className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                                            >
                                                {copied ? <FaCheck size={14} className="text-green-500" /> : <FaCopy size={14} />}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowGenerator(!showGenerator)}
                                                className={`p-1.5 rounded transition-colors ${showGenerator ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                                            >
                                                <FaBolt size={14} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Indicador de fortaleza */}
                                    {formData.password && (
                                        <div className="mt-2">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs text-gray-500">Fortaleza</span>
                                                <span className="text-xs font-medium" style={{ color: getStrengthColor() }}>
                                                    {getStrengthLabel()}
                                                </span>
                                            </div>
                                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full transition-all duration-300"
                                                    style={{
                                                        backgroundColor: getStrengthColor(),
                                                        width: `${passwordStrength}%`
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Generador */}
                                    {showGenerator && (
                                        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-sm font-medium text-gray-700">Generador</span>
                                                <button
                                                    type="button"
                                                    onClick={generatePassword}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-sura-blue text-white text-xs font-medium rounded hover:bg-sura-blue-dark"
                                                >
                                                    <FaSync size={12} />
                                                    Generar
                                                </button>
                                            </div>

                                            <div className="mb-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs text-gray-500">Longitud: {passwordLength}</span>
                                                </div>
                                                <input
                                                    type="range"
                                                    min="8"
                                                    max="32"
                                                    value={passwordLength}
                                                    onChange={(e) => setPasswordLength(parseInt(e.target.value))}
                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sura-blue"
                                                />
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                {Object.entries(passwordOptions).map(([key, enabled]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => setPasswordOptions(prev => ({ ...prev, [key]: !enabled }))}
                                                        className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${enabled
                                                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                                                            : 'bg-white border-gray-200 text-gray-500'
                                                            }`}
                                                    >
                                                        {key === 'lowercase' && 'abc'}
                                                        {key === 'uppercase' && 'ABC'}
                                                        {key === 'numbers' && '123'}
                                                        {key === 'symbols' && '@#$'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Datos de Conexión - dinámico según categoría */}
                    {(showField('url') || showField('ip_address') || showField('puerto') || showField('dominio') || showField('ambiente')) && (
                        <div className="form-modal-section">
                            <h3><FaServer /> Datos de Conexión</h3>
                            <div className="form-modal-grid three-cols">
                                {showField('url') && (
                                    <div className="form-modal-group">
                                        <label htmlFor="url">
                                            <FaGlobe className="input-icon" /> URL
                                        </label>
                                        <input
                                            type="text"
                                            id="url"
                                            name="url"
                                            value={formData.url}
                                            onChange={handleChange}
                                            placeholder="https://ejemplo.com"
                                        />
                                    </div>
                                )}

                                {showField('ip_address') && (
                                    <div className="form-modal-group">
                                        <label htmlFor="ip_address">
                                            <FaServer className="input-icon" /> Dirección IP
                                        </label>
                                        <input
                                            type="text"
                                            id="ip_address"
                                            name="ip_address"
                                            value={formData.ip_address}
                                            onChange={handleChange}
                                            placeholder="192.168.1.1"
                                        />
                                    </div>
                                )}

                                {showField('puerto') && (
                                    <div className="form-modal-group">
                                        <label htmlFor="puerto">
                                            <FaHashtag className="input-icon" /> Puerto
                                        </label>
                                        <input
                                            type="number"
                                            id="puerto"
                                            name="puerto"
                                            value={formData.puerto}
                                            onChange={handleChange}
                                            placeholder="22"
                                        />
                                    </div>
                                )}

                                {showField('dominio') && (
                                    <div className="form-modal-group">
                                        <label htmlFor="dominio">Dominio</label>
                                        <input
                                            type="text"
                                            id="dominio"
                                            name="dominio"
                                            value={formData.dominio}
                                            onChange={handleChange}
                                            placeholder="DOMINIO"
                                        />
                                    </div>
                                )}

                                {showField('ambiente') && (
                                    <div className="form-modal-group">
                                        <label htmlFor="ambiente">Ambiente</label>
                                        <select
                                            id="ambiente"
                                            name="ambiente"
                                            value={formData.ambiente}
                                            onChange={handleChange}
                                        >
                                            <option value="">Seleccione ambiente</option>
                                            {ambientes.map(amb => (
                                                <option key={amb} value={amb}>{amb}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notas y Tags - siempre visible */}
                    <div className="form-modal-section">
                        <h3><FaFileAlt /> Información Adicional</h3>
                        <div className="form-modal-grid">
                            <div className="form-modal-group span-full">
                                <label htmlFor="tags">Tags</label>
                                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 min-h-[44px]">
                                    {formData.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-md"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:text-blue-900"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleAddTag}
                                        className="flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm"
                                        placeholder={formData.tags.length === 0 ? "Escribe y presiona Enter..." : ""}
                                    />
                                </div>
                            </div>

                            <div className="form-modal-group span-full">
                                <label htmlFor="notas">
                                    <FaFileAlt className="input-icon" /> Notas
                                </label>
                                <textarea
                                    id="notas"
                                    name="notas"
                                    value={formData.notas}
                                    onChange={handleChange}
                                    placeholder="Notas adicionales..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-secondary"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    form="credential-form"
                    disabled={saving || !formData.nombre}
                    className="btn btn-primary flex items-center gap-2"
                >
                    {saving ? (
                        <>
                            <FaSync className="animate-spin" size={14} />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FaShieldAlt size={14} />
                            {isEditing ? 'Actualizar' : 'Guardar'}
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default CredentialModal;
